import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useTourStore } from "../store/tourStore";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useT } from "../i18n";
import { useFmt } from "../i18n/fmt";

/** Asistente de configuración inicial de una organización:
 *  1. Bienvenida → 2. Productos del laboratorio (catálogo CIMA) →
 *  3. Competidores sugeridos (misma molécula, otro lab) → 4. Resumen y alta. */

interface LabHit { name: string; product_count: number }
interface CatalogProduct {
  cn_code: string; product_name: string; active_ingredient: string | null;
  laboratory: string | null; atc_code: string | null;
  is_comercializado: boolean; in_portfolio: boolean;
}
interface CompetitorSuggestion {
  cn_code: string; product_name: string; active_ingredient: string | null;
  company_name: string | null; matched_ingredient: string; competitor_type: string;
}

const skipKey = (orgId: string) => `hx_onb_skip:${orgId}`;

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={1.6}
       strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "-2px" }}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const ArrowLeft = () => (
  <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={1.6}
       strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "-2px" }}>
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={1.8}
       strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "-2px" }}>
    <path d="M5 12.5l4.5 4.5L19 6.5" />
  </svg>
);

export default function OnboardingWizardPage() {
  const t = useT();
  const fmt = useFmt();
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const startTour = useTourStore(s => s.start);
  const { isMobile } = useBreakpoint();
  const G = user?.primary_color || "#0891b2";

  const [step, setStep] = useState(0);

  // Paso 2 — laboratorio y productos
  const [labQuery, setLabQuery] = useState("");
  const [labs, setLabs] = useState<LabHit[]>([]);
  const [labsLoading, setLabsLoading] = useState(false);
  const [selectedLab, setSelectedLab] = useState<string | null>(null);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productFilter, setProductFilter] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<Map<string, CatalogProduct>>(new Map());

  // Paso 3 — competidores
  const [suggestions, setSuggestions] = useState<CompetitorSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [selectedComps, setSelectedComps] = useState<Map<string, CompetitorSuggestion>>(new Map());

  // Paso 4 — alta
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readonly = user?.role === "readonly";

  // Autocomplete de laboratorios (debounced)
  useEffect(() => {
    if (labQuery.trim().length < 2) { setLabs([]); return; }
    setLabsLoading(true);
    const t = setTimeout(() => {
      api.get<{ labs: LabHit[] }>(`/portfolio/catalog/labs?q=${encodeURIComponent(labQuery.trim())}`)
        .then(d => setLabs(d.labs))
        .catch(() => setLabs([]))
        .finally(() => setLabsLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [labQuery]);

  function loadProducts(lab: string) {
    setSelectedLab(lab);
    setProductsLoading(true);
    api.get<{ items: CatalogProduct[] }>(`/portfolio/catalog/products?lab=${encodeURIComponent(lab)}`)
      .then(d => setProducts(d.items))
      .catch(() => { setProducts([]); setError(t("onboarding.catalogLoadError")); })
      .finally(() => setProductsLoading(false));
  }

  const visibleProducts = useMemo(() => {
    const f = productFilter.trim().toLowerCase();
    if (!f) return products;
    return products.filter(p =>
      p.product_name.toLowerCase().includes(f) ||
      (p.active_ingredient || "").toLowerCase().includes(f));
  }, [products, productFilter]);

  function toggleProduct(p: CatalogProduct) {
    setSelectedProducts(prev => {
      const next = new Map(prev);
      if (next.has(p.cn_code)) next.delete(p.cn_code); else next.set(p.cn_code, p);
      return next;
    });
  }

  function selectAllComercializados() {
    setSelectedProducts(prev => {
      const next = new Map(prev);
      visibleProducts.filter(p => p.is_comercializado && !p.in_portfolio)
        .forEach(p => next.set(p.cn_code, p));
      return next;
    });
  }

  // Cargar sugerencias de competidores al entrar en el paso 3
  function goToCompetitors() {
    setStep(2);
    const ingredients = [...new Set(
      [...selectedProducts.values()].map(p => (p.active_ingredient || "").trim()).filter(i => i.length >= 3)
    )].join("|");
    if (!ingredients) { setSuggestions([]); return; }
    setSuggestionsLoading(true);
    api.get<{ items: CompetitorSuggestion[] }>(
      `/portfolio/catalog/competitor-suggestions?ingredients=${encodeURIComponent(ingredients)}`)
      .then(d => setSuggestions(d.items))
      .catch(() => setSuggestions([]))
      .finally(() => setSuggestionsLoading(false));
  }

  const suggestionsByIngredient = useMemo(() => {
    const groups = new Map<string, CompetitorSuggestion[]>();
    suggestions.forEach(s => {
      const k = s.matched_ingredient;
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(s);
    });
    return groups;
  }, [suggestions]);

  function toggleComp(s: CompetitorSuggestion) {
    setSelectedComps(prev => {
      const next = new Map(prev);
      if (next.has(s.cn_code)) next.delete(s.cn_code); else next.set(s.cn_code, s);
      return next;
    });
  }

  function toggleGroup(ing: string) {
    const group = suggestionsByIngredient.get(ing) || [];
    const allSelected = group.every(s => selectedComps.has(s.cn_code));
    setSelectedComps(prev => {
      const next = new Map(prev);
      group.forEach(s => { if (allSelected) next.delete(s.cn_code); else next.set(s.cn_code, s); });
      return next;
    });
  }

  async function finishSetup() {
    setSaving(true); setError(null);
    try {
      if (selectedProducts.size > 0) {
        await api.post("/portfolio/bulk", {
          items: [...selectedProducts.values()].map(p => ({
            cn_code: p.cn_code,
            product_name: p.product_name,
            active_ingredient: p.active_ingredient || "—",
            laboratory: p.laboratory,
          })),
        });
      }
      if (selectedComps.size > 0) {
        await api.post("/portfolio/competitors/bulk", {
          items: [...selectedComps.values()].map(s => ({
            cn_code: s.cn_code,
            company_name: s.company_name,
            product_name: s.product_name,
            active_ingredient: s.active_ingredient || s.matched_ingredient,
            competitor_type: "direct",
          })),
        });
      }
      if (user?.organization_id) localStorage.setItem(skipKey(user.organization_id), "done");
      navigate("/dashboard");
      setTimeout(startTour, 600); // encadena con el tutorial de inicio
    } catch (e) {
      setError(e instanceof Error ? e.message : t("onboarding.saveError"));
    } finally {
      setSaving(false);
    }
  }

  function skipWizard() {
    if (user?.organization_id) localStorage.setItem(skipKey(user.organization_id), "skipped");
    navigate("/dashboard");
  }

  const STEP_LABELS = [t("onboarding.stepWelcome"), t("onboarding.stepProducts"), t("onboarding.stepCompetitors"), t("onboarding.stepSummary")];

  const card: React.CSSProperties = {
    background: "var(--clr-surface)",
    backdropFilter: "var(--clr-blur)",
    WebkitBackdropFilter: "var(--clr-blur)",
    border: "1px solid var(--clr-border)",
    borderRadius: 18,
    boxShadow: "var(--clr-shadow-sm)",
    padding: isMobile ? "1.25rem" : "2rem 2.25rem",
  };
  const primaryBtn: React.CSSProperties = {
    background: G, color: "#fff", border: 0, borderRadius: 999,
    padding: "11px 24px", fontSize: "0.9rem", fontWeight: 600, cursor: "pointer", minHeight: 44,
  };
  const ghostBtn: React.CSSProperties = {
    background: "var(--clr-surface)", color: "var(--clr-text3)", border: "1px solid var(--clr-border)",
    borderRadius: 999, padding: "11px 20px", fontSize: "0.85rem", fontWeight: 500, cursor: "pointer", minHeight: 44,
  };

  return (
    <div className="page" style={{ padding: isMobile ? "1rem" : "1.75rem 2rem", maxWidth: 920, margin: "0 auto", fontVariantNumeric: "tabular-nums" }}>
      {/* Indicador de pasos */}
      <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
        {STEP_LABELS.map((label, i) => (
          <div key={label} style={{ flex: 1, textAlign: "center" }}>
            <div style={{ height: 4, borderRadius: 2, background: i <= step ? G : "var(--clr-border)", marginBottom: 6, transition: "background 0.2s" }} />
            {!isMobile && (
              <span style={{ fontSize: "0.7rem", fontWeight: i === step ? 700 : 400, color: i === step ? G : "var(--clr-text4)" }}>
                {i + 1}. {label}
              </span>
            )}
          </div>
        ))}
      </div>

      {error && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.35)", color: "#b91c1c", borderRadius: 12, padding: "0.7rem 1rem", fontSize: "0.83rem", marginBottom: 14 }}>
          {error}
        </div>
      )}

      {/* ── Paso 1: Bienvenida ── */}
      {step === 0 && (
        <div style={{ ...card, textAlign: "center" }}>
          <h2 style={{ margin: "0 0 10px", color: "var(--clr-text)", fontSize: "1.25rem", fontWeight: 700, letterSpacing: "-0.02em" }}>
            {t("onboarding.welcomeTitle")}{user?.organization_name ? `, ${user.organization_name}` : ""}
          </h2>
          <p style={{ color: "var(--clr-text2)", fontSize: "0.92rem", lineHeight: 1.6, maxWidth: 560, margin: "0 auto 10px" }}>
            {t("onboarding.welcomeBody1")}<strong>{t("onboarding.welcomeStrong1")}</strong>{t("onboarding.welcomeBody2")}<strong>{t("onboarding.welcomeStrong2")}</strong>{t("onboarding.welcomeBody3")}
          </p>
          <p style={{ color: "var(--clr-text3)", fontSize: "0.82rem", marginBottom: 22 }}>
            {t("onboarding.welcomeNote")}
          </p>
          {readonly ? (
            <p style={{ color: "#b45309", fontSize: "0.85rem" }}>
              {t("onboarding.readonlyNotice")}
            </p>
          ) : (
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <button style={{ ...primaryBtn, display: "inline-flex", alignItems: "center", gap: 7 }} onClick={() => setStep(1)}>{t("onboarding.startSetup")} <ArrowRight /></button>
              <button style={ghostBtn} onClick={skipWizard}>{t("onboarding.later")}</button>
            </div>
          )}
        </div>
      )}

      {/* ── Paso 2: Productos del laboratorio ── */}
      {step === 1 && (
        <div style={card}>
          <h2 style={{ margin: "0 0 4px", fontSize: "1.15rem", color: "var(--clr-text)" }}>{t("onboarding.labStepTitle")}</h2>
          <p style={{ color: "var(--clr-text3)", fontSize: "0.82rem", margin: "0 0 14px" }}>
            {t("onboarding.labStepDesc")}
          </p>
          <input
            value={labQuery}
            onChange={e => setLabQuery(e.target.value)}
            placeholder={t("onboarding.labSearchPlaceholder")}
            style={{
              width: "100%", boxSizing: "border-box", padding: "11px 14px", fontSize: "0.9rem",
              borderRadius: 12, border: "1px solid var(--clr-border)",
              background: "var(--clr-input-bg)", color: "var(--clr-text)", marginBottom: 10, outline: "none",
            }}
          />
          {labsLoading && <div style={{ color: "var(--clr-text4)", fontSize: "0.8rem" }}>{t("onboarding.searching")}</div>}
          {labs.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
              {labs.map(l => (
                <button key={l.name} onClick={() => loadProducts(l.name)} style={{
                  borderRadius: 999, padding: "8px 14px", fontSize: "0.8rem", cursor: "pointer", minHeight: 38,
                  border: `1px solid ${selectedLab === l.name ? G : "var(--clr-border)"}`,
                  background: selectedLab === l.name ? `${G}18` : "var(--clr-surface2)",
                  color: selectedLab === l.name ? G : "var(--clr-text2)",
                  fontWeight: selectedLab === l.name ? 700 : 400,
                }}>
                  {l.name} <span style={{ opacity: 0.6 }}>({l.product_count})</span>
                </button>
              ))}
            </div>
          )}

          {selectedLab && (
            <>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
                <input
                  value={productFilter}
                  onChange={e => setProductFilter(e.target.value)}
                  placeholder={t("onboarding.productFilterPlaceholder")}
                  style={{
                    flex: 1, minWidth: 160, padding: "8px 12px", fontSize: "0.82rem",
                    borderRadius: 12, border: "1px solid var(--clr-border)",
                    background: "var(--clr-input-bg)", color: "var(--clr-text)", outline: "none",
                  }}
                />
                <button onClick={selectAllComercializados} style={{ ...ghostBtn, minHeight: 38, padding: "8px 12px", fontSize: "0.78rem" }}>
                  {t("onboarding.selectMarketed")}
                </button>
              </div>
              {productsLoading ? (
                <div style={{ color: "var(--clr-text4)", padding: "1rem 0" }}>{t("onboarding.loadingCatalog")}</div>
              ) : (
                <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid var(--clr-border)", borderRadius: 12 }}>
                  {visibleProducts.map(p => {
                    const checked = selectedProducts.has(p.cn_code);
                    return (
                      <label key={p.cn_code} style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                        borderBottom: "1px solid var(--clr-border2)", cursor: p.in_portfolio ? "default" : "pointer",
                        opacity: p.in_portfolio ? 0.5 : 1,
                        background: checked ? `${G}0d` : "transparent", fontSize: "0.82rem",
                      }}>
                        <input type="checkbox" checked={checked || p.in_portfolio} disabled={p.in_portfolio}
                               onChange={() => toggleProduct(p)} style={{ width: 17, height: 17, accentColor: G, flexShrink: 0 }} />
                        <span style={{ flex: 1, color: "var(--clr-text)" }}>
                          {p.product_name}
                          <span style={{ display: "block", color: "var(--clr-text4)", fontSize: "0.72rem" }}>
                            {p.active_ingredient || "—"} · CN {p.cn_code}
                            {p.in_portfolio && ` · ${t("onboarding.alreadyInPortfolio")}`}
                            {!p.is_comercializado && ` · ${t("onboarding.notMarketed")}`}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                  {visibleProducts.length === 0 && (
                    <div style={{ padding: "1.25rem", textAlign: "center", color: "var(--clr-text4)", fontSize: "0.82rem" }}>
                      {t("onboarding.noFilterResults")}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, gap: 8, flexWrap: "wrap" }}>
            <button style={{ ...ghostBtn, display: "inline-flex", alignItems: "center", gap: 6 }} onClick={() => setStep(0)}><ArrowLeft /> {t("onboarding.back")}</button>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: "0.8rem", color: "var(--clr-text3)" }}>
                {selectedProducts.size === 1 ? t("onboarding.productsSelectedOne", { n: selectedProducts.size }) : t("onboarding.productsSelectedMany", { n: selectedProducts.size })}
              </span>
              <button style={{ ...primaryBtn, display: "inline-flex", alignItems: "center", gap: 7, opacity: selectedProducts.size === 0 ? 0.5 : 1 }}
                      disabled={selectedProducts.size === 0} onClick={goToCompetitors}>
                {t("onboarding.continue")} <ArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Paso 3: Competidores ── */}
      {step === 2 && (
        <div style={card}>
          <h2 style={{ margin: "0 0 4px", fontSize: "1.15rem", color: "var(--clr-text)" }}>{t("onboarding.competitorsStepTitle")}</h2>
          <p style={{ color: "var(--clr-text3)", fontSize: "0.82rem", margin: "0 0 14px" }}>
            {t("onboarding.competitorsBody1")}<strong>{t("onboarding.competitorsStrong")}</strong>{t("onboarding.competitorsBody2")}
          </p>
          {suggestionsLoading ? (
            <div style={{ color: "var(--clr-text4)", padding: "1.5rem 0", textAlign: "center" }}>{t("onboarding.searchingCompetitors")}</div>
          ) : suggestions.length === 0 ? (
            <div style={{ color: "var(--clr-text4)", padding: "1.5rem 0", textAlign: "center", fontSize: "0.85rem" }}>
              {t("onboarding.noCompetitors")}
            </div>
          ) : (
            <div style={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
              {[...suggestionsByIngredient.entries()].map(([ing, group]) => {
                const allSelected = group.every(s => selectedComps.has(s.cn_code));
                return (
                  <div key={ing} style={{ border: "1px solid var(--clr-border)", borderRadius: 12, overflow: "hidden" }}>
                    <div style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "8px 12px", background: "var(--clr-surface2)",
                    }}>
                      <strong style={{ fontSize: "0.8rem", color: "var(--clr-text)", textTransform: "uppercase" }}>
                        {ing} <span style={{ color: "var(--clr-text4)", fontWeight: 400 }}>({group.length})</span>
                      </strong>
                      <button onClick={() => toggleGroup(ing)} style={{
                        background: "none", border: "none", color: G, cursor: "pointer",
                        fontSize: "0.74rem", fontWeight: 600, padding: "6px 4px",
                      }}>
                        {allSelected ? t("onboarding.removeAll") : t("onboarding.selectAll")}
                      </button>
                    </div>
                    {group.map(s => {
                      const checked = selectedComps.has(s.cn_code);
                      return (
                        <label key={s.cn_code} style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                          borderTop: "1px solid var(--clr-border2)", cursor: "pointer",
                          background: checked ? `${G}0d` : "transparent", fontSize: "0.82rem",
                        }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleComp(s)}
                                 style={{ width: 17, height: 17, accentColor: G, flexShrink: 0 }} />
                          <span style={{ flex: 1, color: "var(--clr-text)" }}>
                            {s.product_name}
                            <span style={{ display: "block", color: "var(--clr-text4)", fontSize: "0.72rem" }}>
                              {s.company_name || "—"} · CN {s.cn_code}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, gap: 8, flexWrap: "wrap" }}>
            <button style={{ ...ghostBtn, display: "inline-flex", alignItems: "center", gap: 6 }} onClick={() => setStep(1)}><ArrowLeft /> {t("onboarding.back")}</button>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: "0.8rem", color: "var(--clr-text3)" }}>
                {selectedComps.size === 1 ? t("onboarding.compsSelectedOne", { n: selectedComps.size }) : t("onboarding.compsSelectedMany", { n: selectedComps.size })}
              </span>
              <button style={{ ...primaryBtn, display: "inline-flex", alignItems: "center", gap: 7 }} onClick={() => setStep(3)}>{t("onboarding.continue")} <ArrowRight /></button>
            </div>
          </div>
        </div>
      )}

      {/* ── Paso 4: Resumen ── */}
      {step === 3 && (
        <div style={card}>
          <h2 style={{ margin: "0 0 14px", fontSize: "1.15rem", color: "var(--clr-text)" }}>{t("onboarding.summaryStepTitle")}</h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 18 }}>
            <div style={{ border: "1px solid var(--clr-border)", borderRadius: 12, padding: "1rem" }}>
              <div style={{ fontSize: "1.6rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>{fmt.num(selectedProducts.size)}</div>
              <div style={{ fontSize: "0.82rem", color: "var(--clr-text2)", marginBottom: 8 }}>{t("onboarding.portfolioProducts")}</div>
              <div style={{ maxHeight: 180, overflowY: "auto", fontSize: "0.75rem", color: "var(--clr-text3)", lineHeight: 1.7 }}>
                {[...selectedProducts.values()].map(p => <div key={p.cn_code}>· {p.product_name}</div>)}
              </div>
            </div>
            <div style={{ border: "1px solid var(--clr-border)", borderRadius: 12, padding: "1rem" }}>
              <div style={{ fontSize: "1.6rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>{fmt.num(selectedComps.size)}</div>
              <div style={{ fontSize: "0.82rem", color: "var(--clr-text2)", marginBottom: 8 }}>{t("onboarding.competitorsToMonitor")}</div>
              <div style={{ maxHeight: 180, overflowY: "auto", fontSize: "0.75rem", color: "var(--clr-text3)", lineHeight: 1.7 }}>
                {[...selectedComps.values()].map(s => <div key={s.cn_code}>· {s.product_name}</div>)}
              </div>
            </div>
          </div>
          <p style={{ fontSize: "0.8rem", color: "var(--clr-text3)", marginBottom: 18 }}>
            {t("onboarding.summaryNote")}
          </p>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <button style={{ ...ghostBtn, display: "inline-flex", alignItems: "center", gap: 6 }} onClick={() => setStep(2)} disabled={saving}><ArrowLeft /> {t("onboarding.back")}</button>
            <button style={{ ...primaryBtn, display: "inline-flex", alignItems: "center", gap: 7, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={finishSetup}>
              {saving ? t("onboarding.saving") : <><CheckIcon /> {t("onboarding.finishSetup")}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { skipKey as onboardingSkipKey };
